import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Home from '../components/Home';
import RestaurantDetail from '../components/RestaurantDetail';
import Cart from '../components/Cart';
import Checkout from '../components/Checkout';
import OrderTracking from '../components/OrderTracking';
import Register from '../components/Register';
import Login from '../components/Login';
import AnimatedWindow from '../components/AnimatedWindow';
import Empresas from '../Admin/Empresas';
import Usuarios from '../Admin/Usuarios';
import Roles from '../Admin/Roles';
import UsuarioEmpresas from '../Admin/UsuarioEmpresas';
import Clientes from '../Admin/Clientes';
import Negocios from '../Admin/Negocios';
import Empleados from '../Admin/Empleados';
import Productos from '../Admin/Productos';
import PedidosAdmin from '../Admin/PedidosAdmin';
import Dashboard from '../Admin/Dashboard';
import RepartidorView from '../components/RepartidorView';
import './animations.css';
import {
  Restaurant,
  MenuItem,
  CartItem,
  Order,
  CartSummary
} from '../types';
import { RESTAURANTS, MENU_ITEMS } from '../constants';
import { pedidosService } from './services/pedidosService';
import { clientesService } from './services/clientesService';
import Swal from 'sweetalert2';
import { generateTicketPdf } from './utils/generateTicketPdf';

const TrackingRoute: React.FC = () => {
  const { numeroPedido } = useParams<{ numeroPedido: string }>();
  const navigate = useNavigate();
  return <OrderTracking numeroPedido={numeroPedido} onBack={() => navigate('/')} />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // State management
  const [currentView, setCurrentView] = useState<'home' | 'restaurant' | 'cart' | 'checkout' | 'tracking'>('home');
  const [viewHistory, setViewHistory] = useState<Array<'home' | 'restaurant' | 'cart' | 'checkout' | 'tracking'>>(['home']);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [currentOrder, setCurrentOrder] = useState<any>(() => {
    const saved = localStorage.getItem('pendingOrderTracking');
    return saved ? JSON.parse(saved) : null;
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showEmpresas, setShowEmpresas] = useState(false);
  const [showUsuarios, setShowUsuarios] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [showUsuarioEmpresas, setShowUsuarioEmpresas] = useState(false);
  const [showClientes, setShowClientes] = useState(false);
  const [showNegocios, setShowNegocios] = useState(false);
  const [showEmpleados, setShowEmpleados] = useState(false);
  const [showProductos, setShowProductos] = useState(false);
  const [showPedidosAdmin, setShowPedidosAdmin] = useState(false);
  const [showRepartidor, setShowRepartidor] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [checkoutSummary, setCheckoutSummary] = useState<{ includeDelivery: boolean; includeServiceFee: boolean; deliveryFee: number; serviceFee: number; total: number } | null>(null);

  // Restore tracking view if there's a pending order (iOS PWA resume)
  useEffect(() => {
    const saved = localStorage.getItem('pendingOrderTracking');
    if (saved) {
      setCurrentView('tracking');
      setViewHistory(['home', 'tracking']);
    }
  }, []);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('cart');
    }
  }, [cart]);

  const navigateToView = (nextView: 'home' | 'restaurant' | 'cart' | 'checkout' | 'tracking') => {
    setCurrentView(nextView);
    setViewHistory(prev => {
      const last = prev[prev.length - 1];
      if (last === nextView) return prev;
      return [...prev, nextView];
    });

    if (nextView === 'home') {
      setSelectedRestaurant(null);
    }
  };

  const goBack = () => {
    setViewHistory(prev => {
      if (prev.length <= 1) {
        setCurrentView('home');
        setSelectedRestaurant(null);
        return ['home'];
      }

      const next = prev.slice(0, -1);
      const previousView = next[next.length - 1];
      setCurrentView(previousView);

      if (previousView === 'home') {
        setSelectedRestaurant(null);
      }

      return next;
    });
  };

  // Navigation handlers
  const handleHomeClick = () => {
    setViewHistory(['home']);
    setCurrentView('home');
    setSelectedRestaurant(null);
    setCurrentOrder(null);
    localStorage.removeItem('pendingOrderTracking');
  };

  const handleCartClick = () => {
    if (cart.length > 0) {
      navigateToView('cart');
    }
  };

  const handleTrackOrder = () => {
    navigateToView('tracking');
  };

  // Cart operations
  const addToCart = (item: MenuItem) => {
    // Block adding products from a different business
    if (cart.length > 0) {
      const currentRestaurantId = cart[0].restaurantId;
      if (item.restaurantId !== currentRestaurantId) {
        const currentVendor = cart[0].vendorName || 'otro negocio';
        Swal.fire({
          icon: 'warning',
          title: 'Negocio diferente',
          html: `Tu carrito tiene productos de <b>${currentVendor}</b>.<br/>Solo puedes pedir de un negocio a la vez. Vacía tu carrito primero o completa tu pedido actual.`,
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#f97316',
          customClass: {
            popup: 'rounded-2xl'
          }
        });
        return;
      }
    }

    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === item.id);

      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        ) as CartItem[];
      }
      return [...prevCart, {
        ...item,
        quantity: 1,
        vendorName: item.vendorName || 'Restaurante'
      }];
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === id);
      if (!existingItem) return prevCart;

      const newQuantity = existingItem.quantity + delta;

      if (newQuantity <= 0) {
        return prevCart.filter(item => item.id !== id);
      }

      return prevCart.map(item =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      );
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prevCart => prevCart.filter(item => item.id !== id));
  };

  // Checkout flow
  const handleCheckout = (opts: { includeDelivery: boolean; includeServiceFee: boolean; deliveryFee: number; serviceFee: number; total: number }) => {
    setCheckoutSummary(opts);
    navigateToView('checkout');
  };

  const handleConfirmOrder = async (paymentMethod: string, deliveryAddress: string, subtotal: number, importeEnvio: number, tarifaServicio: number) => {
    const phone = localStorage.getItem('deliveryPhone') || '';
    if (!phone) {
      Swal.fire({ icon: 'warning', title: 'Teléfono requerido', text: 'Ingresa tu dirección y teléfono antes de hacer un pedido.', confirmButtonColor: '#f97316', customClass: { popup: 'rounded-2xl' } });
      return;
    }
    try {
      // Auto-register cliente if needed
      let clienteId = user?.clienteId;
      if (!clienteId) {
        const registro = await clientesService.autoRegistro({
          telefono: phone,
          nombre: user?.name || 'Cliente',
          direccion: deliveryAddress || null,
        });
        clienteId = registro.clienteId;
      }

      const total = subtotal + importeEnvio + tarifaServicio;
      
      // Create order in backend
      const negocioId = cart.length > 0 ? parseInt(cart[0].restaurantId, 10) : (selectedRestaurant ? parseInt(selectedRestaurant.id, 10) : 0);
      const pedido = await pedidosService.create({
        clienteId,
        negocioId,
        direccionEntrega: deliveryAddress,
        subtotal,
        importeEnvio,
        tarifaServicio,
        total,
        latitud: parseFloat(localStorage.getItem('deliveryLat') || '19.4326'),
        longitud: parseFloat(localStorage.getItem('deliveryLng') || '-99.1332'),
        detalles: cart.map(item => ({
          nombreProducto: item.name,
          precioUnitario: item.price,
          cantidad: item.quantity,
          imagenUrl: item.image || null,
          productoId: parseInt(item.id, 10) || null,
        })),
      });

      // Save order tracking data BEFORE PDF (iOS PWA may lose state)
      const orderData = {
        pedidoId: pedido.pedidoId,
        numeroPedido: pedido.numeroPedido,
        items: cart,
        status: pedido.estatus,
        paymentMethod,
        deliveryAddress,
        estimatedDelivery: pedido.llegadaEstimada ? new Date(pedido.llegadaEstimada) : new Date(Date.now() + 45 * 60 * 1000),
        total: pedido.total,
        orderDate: new Date(pedido.fechaCreacion)
      };
      setCurrentOrder(orderData);
      localStorage.setItem('pendingOrderTracking', JSON.stringify(orderData));

      navigateToView('tracking');
      setCart([]); // Clear cart after order
      localStorage.removeItem('cart');

      // Generate PDF ticket AFTER tracking view renders (delay for iOS PWA)
      const pdfItems = [...cart];
      const nombreNegocio = cart.length > 0 ? cart[0].vendorName : 'Negocio';
      setTimeout(() => {
        generateTicketPdf({
          numeroPedido: pedido.numeroPedido,
          fecha: new Date(pedido.fechaCreacion),
          items: pdfItems,
          subtotal,
          importeEnvio,
          tarifaServicio,
          total,
          direccionEntrega: deliveryAddress,
          telefono: phone,
          metodoPago: paymentMethod,
          nombreNegocio,
        });
      }, 800);
      
    } catch (error) {
      console.error('Error creating order:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al crear pedido',
        text: error instanceof Error ? error.message : 'No se pudo procesar tu pedido',
        confirmButtonColor: '#f97316',
        background: 'rgba(15,23,42,0.85)',
        color: '#fff',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    }
  };

  // Calculate cart total
  const calculateCartTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const deliveryFee = 40; // Fixed delivery fee
    const serviceFee = 15; // Service fee
    return {
      subtotal,
      deliveryFee,
      serviceFee,
      total: subtotal + deliveryFee + serviceFee
    };
  };

  // Render current view
  const renderView = () => {
    switch (currentView) {
      case 'home':
        return (
          <AnimatedWindow isOpen={currentView === 'home'}>
            <Home
              searchQuery={searchQuery}
              onRestaurantSelect={(restaurant) => {
                setSelectedRestaurant(restaurant);
                navigateToView('restaurant');
              }}
            />
          </AnimatedWindow>
        );

      case 'restaurant':
        return (
          <AnimatedWindow isOpen={currentView === 'restaurant'}>
            {selectedRestaurant && (
              <RestaurantDetail
                restaurant={selectedRestaurant}
                menuItems={MENU_ITEMS.filter(item => item.restaurantId === selectedRestaurant.id)}
                onAddToCart={addToCart}
                onBack={goBack}
              />
            )}
          </AnimatedWindow>
        );

      case 'cart':
        return (
          <AnimatedWindow isOpen={currentView === 'cart'}>
            <Cart
              cart={cart}
              onUpdateQuantity={updateQuantity}
              onRemove={removeFromCart}
              onGoToCheckout={handleCheckout}
              onBackToMenu={goBack}
              onTrackOrder={currentOrder ? handleTrackOrder : undefined}
              cartSummary={calculateCartTotal()}
            />
          </AnimatedWindow>
        );

      case 'checkout':
        return (
          <AnimatedWindow isOpen={currentView === 'checkout'}>
            <Checkout
              cart={cart}
              cartTotal={checkoutSummary?.total ?? calculateCartTotal().total}
              includeDelivery={checkoutSummary?.includeDelivery ?? false}
              includeServiceFee={checkoutSummary?.includeServiceFee ?? false}
              deliveryFee={checkoutSummary?.deliveryFee ?? 40}
              serviceFee={checkoutSummary?.serviceFee ?? 15}
              onConfirm={handleConfirmOrder}
              onBack={goBack}
            />
          </AnimatedWindow>
        );

      case 'tracking':
        return (
          <AnimatedWindow isOpen={currentView === 'tracking'}>
            {currentOrder ? (
              <OrderTracking
                pedidoId={currentOrder.pedidoId}
                numeroPedido={currentOrder.numeroPedido || currentOrder.id}
                onBack={handleHomeClick}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-lg text-gray-600 mb-4">No hay pedido para rastrear</p>
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-primary"
                >
                  Iniciar sesión
                </Link>
              </div>
            )}
          </AnimatedWindow>
        );

      default:
        return (
          <AnimatedWindow isOpen={currentView === 'home'}>
            <Home searchQuery={searchQuery} onRestaurantSelect={() => { }} />
          </AnimatedWindow>
        );
    }
  };

  const showNavbar = !['/register', '/login'].includes(location.pathname);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark transition-colors duration-300">
      {showNavbar && (
        <Navbar
          onHomeClick={handleHomeClick}
          onCartClick={handleCartClick}
          cartCount={cart.length}
          isAuthenticated={isAuthenticated}
          showSearch={currentView === 'home'}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onEmpresasClick={() => setShowEmpresas(true)}
          onUsuariosClick={() => setShowUsuarios(true)}
          onRolesClick={() => setShowRoles(true)}
          onUsuarioEmpresasClick={() => setShowUsuarioEmpresas(true)}
          onClientesClick={() => setShowClientes(true)}
          onNegociosClick={() => setShowNegocios(true)}
          onEmpleadosClick={() => setShowEmpleados(true)}
          onProductosClick={() => setShowProductos(true)}
          onPedidosAdminClick={() => setShowPedidosAdmin(true)}
          onRepartidorClick={() => setShowRepartidor(true)}
          onDashboardClick={() => setShowDashboard(true)}
        />
      )}
      {showEmpresas && <Empresas onClose={() => setShowEmpresas(false)} />}
      {showUsuarios && <Usuarios onClose={() => setShowUsuarios(false)} />}
      {showRoles && <Roles onClose={() => setShowRoles(false)} />}
      {showUsuarioEmpresas && <UsuarioEmpresas onClose={() => setShowUsuarioEmpresas(false)} />}
      {showClientes && <Clientes onClose={() => setShowClientes(false)} />}
      {showNegocios && <Negocios onClose={() => setShowNegocios(false)} />}
      {showEmpleados && <Empleados onClose={() => setShowEmpleados(false)} />}
      {showProductos && <Productos onClose={() => setShowProductos(false)} />}
      {showPedidosAdmin && <PedidosAdmin onClose={() => setShowPedidosAdmin(false)} />}
      {showRepartidor && <RepartidorView onClose={() => setShowRepartidor(false)} />}
      {showDashboard && <Dashboard onClose={() => setShowDashboard(false)} />}
      <main className={showNavbar ? 'pb-16' : ''}>
        <Routes>
          <Route path="/register" element={
            <AnimatedWindow isOpen={location.pathname === '/register'}>
              <Register />
            </AnimatedWindow>
          } />
          <Route path="/login" element={
            <AnimatedWindow isOpen={location.pathname === '/login'}>
              <Login />
            </AnimatedWindow>
          } />
          <Route path="/tracking/:numeroPedido" element={<TrackingRoute />} />
          <Route path="/" element={
            <>
              {renderView()}
            </>
          } />
        </Routes>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
